import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import {
  downloadCandidateResume,
  downloadCandidateVideo,
  generateAIReport,
} from '@/modules/hrPortal/api/candidates.api';

import {
  updateCandidateStatus as updateCandidateStatusThunk,
} from '@/modules/hrPortal/store/candidates.thunk';

import {
  fetchCandidateDetails,
  updateHRNotes,
} from '@/modules/hrPortal/store/candidateDetails.thunk';

interface UseCandidateDetailsActionsOptions {
  onMessage?: (message: string | null) => void;
}

export default function useCandidateDetailsActions(
  applicationId: string,
  options: UseCandidateDetailsActionsOptions = {}
) {
  const dispatch = useDispatch<any>();

  const {
    onMessage,
  } = options;

  const [statusUpdating, setStatusUpdating] =
    useState(false);

  const [aiGenerating, setAiGenerating] =
    useState(false);

  const aiGeneratingRef = useRef(false);

  useEffect(() => {
    return () => {
      aiGeneratingRef.current = false;
    };
  }, []);

  // --------------------------------------------------
  // Update Candidate Status
  // --------------------------------------------------

  const updateCandidateStatus = async (
    status: string,
    reason?: string
  ) => {
    if (!applicationId) {
      return false;
    }

    try {
      setStatusUpdating(true);

      onMessage?.(null);

      await dispatch(
        updateCandidateStatusThunk({
          applicationId,
          status,
          reason,
        })
      ).unwrap();

      await dispatch(
        fetchCandidateDetails(applicationId)
      ).unwrap();

      toast.success(
        `Candidate status updated to ${status}.`
      );

      return true;
    } catch (error: any) {
      console.error(
        'Status update failed:',
        error
      );

      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message ||
            'Unable to update candidate status.';

      toast.error(errorMessage);

      return false;
    } finally {
      setStatusUpdating(false);
    }
  };

  // --------------------------------------------------
  // Save HR Notes
  // --------------------------------------------------

  const saveNotes = async (
    notes: string
  ) => {
    if (!applicationId) {
      return false;
    }

    try {
      onMessage?.(null);

      await dispatch(
        updateHRNotes({
          applicationId,
          hrNotes: notes,
        })
      ).unwrap();

      toast.success(
        'HR notes saved successfully.'
      );

      return true;
    } catch (error: any) {
      console.error(
        'HR notes save failed:',
        error
      );

      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message ||
            'Unable to save HR notes.';

      toast.error(errorMessage);

      return false;
    }
  };

  // --------------------------------------------------
  // Generate AI Report
  // --------------------------------------------------

  const handleGenerateAIReport = async () => {
    if (!applicationId) {
      return false;
    }

    if (aiGeneratingRef.current) {
      return false;
    }

    try {
      setAiGenerating(true);

      aiGeneratingRef.current = true;

      onMessage?.(null);

      // --------------------------------------------------
      // 1. Backend starts AI generation and waits until
      //    the complete AI pipeline finishes.
      // --------------------------------------------------

      await generateAIReport(applicationId);

      // --------------------------------------------------
      // 2. Fetch final candidate details ONCE.
      //
      //    No frontend polling.
      // --------------------------------------------------

      await dispatch(
        fetchCandidateDetails(applicationId)
      ).unwrap();

      // --------------------------------------------------
      // 3. Success
      // --------------------------------------------------

      toast.success(
        'AI report generated successfully.'
      );

      return true;
    } catch (error: any) {
      console.error(
        'AI report generation failed:',
        error
      );

      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message ||
            'Unable to generate AI report.';

      toast.error(errorMessage);

      return false;
    } finally {
      aiGeneratingRef.current = false;
      setAiGenerating(false);
    }
  };

  // --------------------------------------------------
  // Download Resume / Video
  // --------------------------------------------------

  const downloadDocument = async (
    type: 'resume' | 'video',
    fileName?: string
  ) => {
    if (!applicationId) {
      return;
    }

    try {
      const response =
        type === 'resume'
          ? await downloadCandidateResume(
              applicationId
            )
          : await downloadCandidateVideo(
              applicationId
            );

      const contentType =
        response.headers?.['content-type'];

      const blob = new Blob(
        [response.data],
        {
          type:
            typeof contentType === 'string'
              ? contentType
              : 'application/octet-stream',
        }
      );

      const url =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement('a');

      link.href = url;

      link.download =
        fileName ||
        (type === 'resume'
          ? 'resume'
          : 'video');

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        `Unable to download ${type}`,
        error
      );

      toast.error(
        `Unable to download ${type}.`
      );
    }
  };

  // --------------------------------------------------
  // Return
  // --------------------------------------------------

  return {
    statusUpdating,
    updateCandidateStatus,
    saveNotes,
    downloadDocument,
    aiGenerating,
    generateAIReport: handleGenerateAIReport,
  };
}